import { isSystemDataSequenceId } from "building-types/dataSequences/isSystemDataSequenceId";
import type { DocEntityType } from "building-types/doc/docEntityTypes";
import type { DocEntityBase } from "building-types/entities/docEntityBase";
import type { Group } from "building-types/groups/group";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { isSystemContentTemplateId } from "./contentTemplateTools";
import {
  isBuiltInCipComponent,
  isBuiltInMasonryComponent,
  isBuiltInSlabFloorComponent,
  isBuiltInSheathingComponent,
} from "./defaults/assemblyComponentDefaults";
import { isBuiltInFrameFloorAssembly, isBuiltInSlabFloorAssembly } from "./defaults/defaultFloorAssembly";
import { isBuiltInWallPlaneAssembly } from "./defaults/defaultWallPlaneAssembly";
import type {
  SystemEntityEligibleEntity,
  SystemEntityEligibleEntityHandle,
  SystemEntityEligibleEntityType,
} from "./docSystemEntityTypes";
import { systemEntityEligibleEntities } from "./docSystemEntityTypes";
import { isBuiltInFoundationAssembly } from "./elements/foundations/defaultFoundationAssemblies";
import { isBuiltInFoundationComponent } from "./elements/foundations/defaultFoundationComponents";
import { isBuiltInFramingComponent } from "./elements/framing/defaultFramingComponents";
import { isBuiltInHeaderAssembly } from "./elements/framing/defaultHeaderAssembly";
import { isSystemDefaultProfile } from "./elements/trim/defaultTrimProfiles";
import { isBasePresetId } from "./options/presetTools";
import { isSystemOptionSet } from "./options/systemOptionSet";
import { isBuiltInLabel } from "./utils/builtInLabelUtils";
import { isBuiltInDatum } from "./utils/datumTools";

export function isSystemEntity<T extends DocEntityBase>(entity: T): boolean {
  if (!isSystemEntityEligibleEntityType(entity.entityType)) {
    return false;
  }

  switch (entity.entityType) {
    case "GROUP":
      return groupIsSystemGroup(entity as Group);
    case "OPTION_SET":
      return isSystemOptionSet({ type: "OPTION_SET", id: entity.id });
    case "LABEL":
      return isBuiltInLabel(entity.id);
    case "PRESET":
      return isBasePresetId(entity.id);
    case "DATUM":
      return isBuiltInDatum(entity.id);
    case "FRAMING_COMPONENT":
      return isBuiltInFramingComponent(entity.id);
    case "FOUNDATION_ASSEMBLY":
      return isBuiltInFoundationAssembly(entity.id);
    case "FOUNDATION_COMPONENT":
      return isBuiltInFoundationComponent(entity.id);
    case "HEADER_ASSEMBLY":
      return isBuiltInHeaderAssembly(entity.id);
    case "SLAB_COMPONENT":
      return isBuiltInSlabFloorComponent(entity.id);
    case "SHEATHING_COMPONENT":
      return isBuiltInSheathingComponent(entity.id);
    case "MASONRY_COMPONENT":
      return isBuiltInMasonryComponent(entity.id);
    case "CIP_COMPONENT":
      return isBuiltInCipComponent(entity.id);
    case "FRAME_FLOOR_ASSEMBLY":
      return isBuiltInFrameFloorAssembly(entity.id);
    case "SLAB_FLOOR_ASSEMBLY":
      return isBuiltInSlabFloorAssembly(entity.id);
    case "WALL_PLANE_ASSEMBLY":
      return isBuiltInWallPlaneAssembly(entity.id);
    case "SYSTEM_PARAMETER":
      return true;
    case "DATA_SEQUENCE":
      return isSystemDataSequenceId(entity.id);
    case "CONTENT_TEMPLATE":
      return isSystemContentTemplateId(entity.id);
    case "PROFILE":
      return isSystemDefaultProfile(entity.id);
    default:
      return false;
  }
}

export function groupIsSystemGroup(group: Group): boolean {
  return !!group.isBuiltIn || !!group.isContentMutableBuiltIn;
}

export function isSystemEntityEligibleEntityType(type: DocEntityType): type is SystemEntityEligibleEntityType {
  return systemEntityEligibleEntities[type];
}

export function isSystemEntityEligibleEntity(entity: SystemEntityEligibleEntity): entity is SystemEntityEligibleEntity {
  return isSystemEntityEligibleEntityType(entity.entityType);
}

export function isSystemEntityEligibleEntityHandle(
  handle: DocEntityHandle,
): handle is SystemEntityEligibleEntityHandle {
  return isSystemEntityEligibleEntityType(handle.type);
}
